import { CenteredLoader } from '@library/components'
import { getToken } from '@library/token'
import type { Messenger } from 'common/messenger'
import type { Nullable } from 'common/types'
import type { FC, ReactElement } from 'react'
import { useCallback, useEffect, useState } from 'react'

import { getKeycloakConfig } from '../Auth/getKeycloakConfig'
import type { AuthState, KeycloakConfig } from '../Auth/types'
import { useKeycloakConfig } from '../Auth/useKeycloakConfig'
import css from './AuthProvider.css'

const initial: AuthState = {
  token: null,
  authenticated: false,
  user: { username: null },
  loginUrl: null,
  logoutUrl: null,
  login: null,
  logout: null,
  userInfo: null,
  features: null,
  currentContext: null,
  tokenParsed: null,
}

type AuthProviderProps = {
  coreMessenger?: Messenger
  children?: (
    getToken: () => Nullable<string> | undefined,
    logout: () => Promise<void> | undefined,
  ) => ReactElement
}

export const AuthProvider: FC<AuthProviderProps> = ({ coreMessenger, children }) => {
  const [currentUser, setCurrentUser] = useState<AuthState>(initial)
  const [keycloakConfig, setKeycloakConfig] = useState<Nullable<KeycloakConfig>>(null)

  useEffect(() => {
    getKeycloakConfig().then(setKeycloakConfig)
  }, [])

  const handleUserSet = useCallback(
    (user: AuthState) => {
      setCurrentUser(user)
      coreMessenger?.send({
        type: 'userLoggedIn',
        payload: undefined,
      })
    },
    [coreMessenger],
  )

  useKeycloakConfig(keycloakConfig, handleUserSet)

  const logout = useCallback(() => currentUser.logout?.(), [currentUser])

  if (currentUser.authenticated && currentUser.token) {
    return children?.(getToken, logout) ?? null
  }

  return (
    <div className={css.app}>
      <CenteredLoader />
    </div>
  )
}
